import axios from 'axios';

// Support both REACT_APP_BACKEND_URL and REACT_APP_BACKEND_URI (backward compatibility)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URI;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with no-cache headers
const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0',
  },
});

// Add request interceptor to attach token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default apiClient;

// Auth APIs
export const authAPI = {
  register: (data) => apiClient.post('/auth/register', data),
  login: (data) => apiClient.post('/auth/login', data),
  getMe: () => apiClient.get('/auth/me'),
  init: () => apiClient.get('/auth/init'),
  changePassword: (data) => apiClient.post('/auth/change-password', data),
  forgotPassword: (data) => apiClient.post('/auth/forgot-password', data),
};

// Partial Exam APIs
export const partialExamAPI = {
  getSlots: (params) => apiClient.get('/partial/slots', { params }),
  book: (partialId) => apiClient.post('/partial/book', { partial_id: partialId }),
  cancel: (data) => apiClient.post('/partial/cancel', data),
  getCancellationInfo: () => apiClient.get('/partial/cancellation-info'),
  getMyBookings: () => apiClient.get('/partial/my-bookings'),
  create: (data) => apiClient.post('/partial/create', data),
  getSpeakingDates: () => apiClient.get('/partial/speaking-dates'),
  getSpeakingSlotsByDate: (date) => apiClient.get(`/partial/speaking-slots/${date}`),
};

// For backward compatibility
export const partialAPI = partialExamAPI;

// Mock Exam APIs
export const mockExamAPI = {
  getList: (params) => apiClient.get('/mock/list', { params }),
  getSpeakingSlots: (mockId) => apiClient.get(`/mock/${mockId}/speaking-slots`),
  bookLRW: (mockId) => apiClient.post('/mock/book', { mock_id: mockId }),
  bookSpeaking: (mockBookingId, speakSlotId, mode = 'OFFLINE', whatsappNumber = null) => 
    apiClient.post('/mock/speaking/book', { 
      mock_booking_id: mockBookingId, 
      speak_slot_id: speakSlotId,
      speak_mode: mode,
      whatsapp_number: whatsappNumber
    }),
  cancel: (data) => apiClient.post('/mock/cancel', data),
  getMyBookings: () => apiClient.get('/mock/my-bookings'),
  create: (data) => apiClient.post('/mock/create', data),
};

// For backward compatibility
export const mockAPI = mockExamAPI;

// Admin APIs
export const adminAPI = {
  getStats: () => apiClient.get('/admin/dashboard/stats'),
  adjustCredits: (userId, delta, reason) => 
    apiClient.post('/admin/credits/adjust', { user_id: userId, delta, reason }),
  getStudents: (params) => apiClient.get('/admin/students', { params }),
  exportPartialCSV: (params) => 
    apiClient.get('/admin/export/partial-csv', { params, responseType: 'blob' }),
  exportMockCSV: (params) => 
    apiClient.get('/admin/export/mock-csv', { params, responseType: 'blob' }),
  getActivityLogs: (limit = 50) => apiClient.get('/admin/activity-logs', { params: { limit } }),
  getExamsByModule: (module) => apiClient.get(`/admin/exams?module=${module}`),
  getPartialExams: () => apiClient.get('/admin/exams'),
  getMocks: () => apiClient.get('/admin/mocks'),
  manualBookPartial: (bookingData) => 
    apiClient.post('/admin/manual-booking/partial', bookingData),
  manualBookMock: (bookingData) => 
    apiClient.post('/admin/manual-booking/mock', bookingData),
  getAvailablePartialSlots: (params) => apiClient.get('/admin/available-slots/partial', { params }),
  getAvailableMockSlots: (params) => apiClient.get('/admin/available-slots/mock', { params }),
  getMockSpeakingSlots: (mockId) => apiClient.get(`/admin/mock/${mockId}/speaking-slots`),
  deleteSpeakingSlot: (slotId) => apiClient.delete(`/admin/speaking-slot/${slotId}`),
  deleteStudent: (studentId) => apiClient.delete(`/admin/students/${studentId}`),
  toggleStudentActive: (studentId) => apiClient.put(`/admin/students/${studentId}/toggle-active`),
  updateStudent: (studentId, data) => apiClient.put(`/admin/students/${studentId}`, data),
  deletePartialExam: (examId) => apiClient.delete(`/admin/partial-exam/${examId}`),
  deleteMockExam: (examId) => apiClient.delete(`/admin/mock-exam/${examId}`),
  decreaseMockSeats: (examId) => apiClient.put(`/admin/mock-exam/${examId}/decrease-seats`),
  increaseMockSeats: (examId) => apiClient.put(`/admin/mock-exam/${examId}/increase-seats`),
  forceDeleteMockExam: (examId) => apiClient.delete(`/migration/force-delete-mock/${examId}`),
  exportBatchCSV: (batchName) => apiClient.get(`/admin/batch/${batchName}/export-csv`, { responseType: 'blob' }),
  updateBatchCredits: (batchId, credits) => apiClient.put(`/admin/batch/${batchId}/credits`, credits),
  updateStudentModuleCredits: (studentId, credits) => apiClient.put(`/admin/students/${studentId}/module-credits`, credits),
  getStudent: (studentId) => apiClient.get(`/admin/students/${studentId}`),
  // Moderator Management
  getModerators: () => apiClient.get('/admin/moderators'),
  createModerator: (data) => apiClient.post('/admin/moderators/create', data),
  updateModerator: (moderatorId, data) => apiClient.put(`/admin/moderators/${moderatorId}/update`, data),
  updateModeratorPermissions: (moderatorId, permissions) => apiClient.put(`/admin/moderator/${moderatorId}/permissions`, permissions),
  toggleModeratorStatus: (moderatorId) => apiClient.put(`/admin/moderators/${moderatorId}/toggle-status`),
  deleteModerator: (moderatorId) => apiClient.delete(`/admin/moderators/${moderatorId}`),

  // Activity Logs
  getActivityLogs: (params) => apiClient.get('/admin/activity-logs/', { params }),
  getActivityLogStats: () => apiClient.get('/admin/activity-logs/stats'),
  getActivityLogActionTypes: () => apiClient.get('/admin/activity-logs/action-types'),
  cleanupActivityLogs: (months) => apiClient.post(`/admin/activity-logs/cleanup?months=${months}`),
  
  // Email Queue Management
  getEmailQueue: () => apiClient.get('/admin/email-queue'),
  retryEmailQueue: () => apiClient.post('/admin/email-queue/retry'),
  clearEmailQueue: () => apiClient.delete('/admin/email-queue/clear'),
  
  // Student Messages Management
  getMessages: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiClient.get(`/admin/messages?${query}`);
  },
  getMessageDetail: (messageId) => apiClient.get(`/admin/messages/${messageId}`),
  toggleMessageSolved: (messageId) => apiClient.put(`/admin/messages/${messageId}/solve`),
  deleteMessage: (messageId) => apiClient.delete(`/admin/messages/${messageId}`),
  bulkDeleteMessages: (messageIds) => apiClient.post('/admin/messages/bulk-delete', messageIds),
  getMessageAudio: (messageId) => apiClient.get(`/admin/messages/audio/${messageId}`),
  
  // Export Numbers
  getStudentsByCategory: (category) => apiClient.get(`/export/students/preview?category=${category}`),
  exportStudentNumbers: (category) => apiClient.get(`/export/students/csv?category=${category}`, { responseType: 'blob' }),
  exportStudentPhonesTxt: (category) => apiClient.get(`/export/students/phones-txt?category=${category}`, { responseType: 'blob' }),
  
  // Exam Delete
  deletePartialExam: (examId) => apiClient.delete(`/admin/exams/partial/${examId}`),
  deleteMockExam: (examId) => apiClient.delete(`/admin/exams/mock/${examId}`),
  
  // Active/Past Exams
  getActivePartialExams: () => apiClient.get('/admin/exams/partial/active'),
  getPastPartialExams: (page = 1, limit = 20) => apiClient.get(`/admin/exams/partial/past?page=${page}&limit=${limit}`),
  getActiveMockExams: () => apiClient.get('/admin/exams/mock/active'),
  getPastMockExams: (page = 1, limit = 20) => apiClient.get(`/admin/exams/mock/past?page=${page}&limit=${limit}`),
  exportPastExamBookings: (examType, examId) => apiClient.get(`/admin/exams/past/export?exam_type=${examType}&exam_id=${examId}`, { responseType: 'blob' }),
  
  // System Tools / Migrations
  syncMockSpeakingSlots: (dryRun = false) => apiClient.post(`/migration/sync-mock-speaking-slots?dry_run=${dryRun}`),
  fixPartialBookingIndex: () => apiClient.post('/migration/fix-partial-booking-index'),
  debugMockSpeaking: (mockId) => apiClient.get(`/migration/debug-mock-speaking/${mockId}`),
  debugSpecificMock: (mockId) => apiClient.get(`/migration/debug-specific-mock/${mockId}`),
  fullMockAudit: (mockId) => apiClient.get(`/migration/full-mock-audit/${mockId}`),
  diagnoseRestoreIssue: (mockId) => apiClient.get(`/migration/diagnose-restore-issue/${mockId}`),
  fixUniqueUserMockIndex: () => apiClient.post('/migration/fix-unique-user-mock-index'),
  restoreCancelledMockBookings: (mockId, dryRun = false) => apiClient.post(`/migration/restore-cancelled-mock-bookings/${mockId}?dry_run=${dryRun}`),
  convertExamCreatedAtToBdTime: (dryRun = true) => apiClient.post(`/migration/convert-exam-created-at-to-bd-time?dry_run=${dryRun}`),
  exportMockStudentsCSV: (mockId, filters = {}) => {
    const params = new URLSearchParams(filters);
    return apiClient.get(`/admin/mock/${mockId}/export-csv?${params.toString()}`, { responseType: 'blob' });
  },
  getSpeakingDates: () => apiClient.get('/admin/partial/speaking-dates'),
  getSpeakingSlotsByDate: (date) => apiClient.get(`/admin/partial/speaking-slots/${date}`),
  // New Exam Notification APIs
  sendNewExamNotifications: (createdAfter = null) => {
    const params = createdAfter ? `?created_after=${encodeURIComponent(createdAfter)}` : '';
    return apiClient.post(`/admin/send-new-exam-notifications${params}`);
  },
  getPendingExamNotifications: (createdAfter = null) => {
    const params = createdAfter ? `?created_after=${encodeURIComponent(createdAfter)}` : '';
    return apiClient.get(`/admin/pending-exam-notifications${params}`);
  },
};

// Batch APIs
export const batchAPI = {
  create: (data) => apiClient.post('/batch/create', data),
  getList: () => apiClient.get('/batch/list'),
  get: (batchId) => apiClient.get(`/batch/${batchId}`),
  update: (batchId, data) => apiClient.put(`/batch/${batchId}`, data),
  delete: (batchId) => apiClient.delete(`/batch/${batchId}`),
  bulkRegister: (data) => apiClient.post('/batch/bulk-register', data),
  getStudents: (batchId) => apiClient.get(`/batch/${batchId}/students`),
  extendStudentValidity: (studentId, months) => apiClient.post(`/batch/students/${studentId}/extend-validity`, { months }),
  deleteSpeakingSlot: (slotId) => apiClient.delete(`/mock/speaking-slot/${slotId}`),
};


// Password Reset APIs
export const requestPasswordReset = (email) => 
  apiClient.post('/password-reset/request', { email });

export const verifyResetToken = (token) => 
  apiClient.post(`/password-reset/verify-token?token=${token}`);

export const resetPassword = (data) => 
  apiClient.post('/password-reset/reset', data);

// Notice APIs (Admin)
export const noticeAPI = {
  // Admin endpoints
  create: (data) => apiClient.post('/notices/admin/create', data),
  createWithImage: (formData, queryParams) => apiClient.post(`/notices/admin/create-with-image?${queryParams}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  list: () => apiClient.get('/notices/admin/list'),
  get: (noticeId) => apiClient.get(`/notices/admin/${noticeId}`),
  update: (noticeId, data) => apiClient.put(`/notices/admin/${noticeId}`, data),
  togglePublish: (noticeId) => apiClient.put(`/notices/admin/${noticeId}/publish`),
  delete: (noticeId) => apiClient.delete(`/notices/admin/${noticeId}`),
  getStats: (noticeId) => apiClient.get(`/notices/admin/${noticeId}/stats`),
  // Student endpoints
  getActiveNotices: () => apiClient.get('/notices/active'),
  markViewed: (noticeId) => apiClient.post(`/notices/viewed/${noticeId}`),
};

// Credit History API (Student)
export const creditHistoryAPI = {
  getMyHistory: (limit = 50) => apiClient.get(`/credit-history/my-history?limit=${limit}`),
  getSummary: () => apiClient.get('/credit-history/summary'),
};

// Site Settings API
export const settingsAPI = {
  getSiteSettings: () => apiClient.get('/settings/site'),
  updateDevToolsSetting: (enabled) => apiClient.put('/settings/dev-tools', { enabled }),
};

// External Booking API (Public + Admin)
export const externalBookingAPI = {
  // Public
  getMockExams: () => apiClient.get('/external/mock-exams'),
  getSpeakingSlots: (mockId) => apiClient.get(`/external/mock-exams/${mockId}/speaking-slots`),
  getPublicSettings: () => apiClient.get('/external/public-settings'),
  submitBooking: (formData) => apiClient.post('/external/book', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Admin
  getApprovals: (status) => apiClient.get(`/external/approvals?status_filter=${status || 'pending'}`),
  approveBooking: (bookingId) => apiClient.post(`/external/approvals/${bookingId}/approve`),
  rejectBooking: (bookingId, reason) => {
    const formData = new FormData();
    if (reason) formData.append('reason', reason);
    return apiClient.post(`/external/approvals/${bookingId}/reject`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getSettings: () => apiClient.get('/external/settings'),
  updateSettings: (data) => {
    const formData = new FormData();
    if (data.mock_fee !== undefined) formData.append('mock_fee', data.mock_fee);
    if (data.bkash_number) formData.append('bkash_number', data.bkash_number);
    if (data.max_file_size_mb !== undefined) formData.append('max_file_size_mb', data.max_file_size_mb);
    if (data.show_seats !== undefined) formData.append('show_seats', data.show_seats ? 'true' : 'false');
    return apiClient.put('/external/settings', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // File management
  listFiles: () => apiClient.get('/external/files'),
  deleteFile: (bookingId) => apiClient.delete(`/external/files/${bookingId}`),
  cleanupOldFiles: (days) => apiClient.delete(`/external/files/cleanup/older-than?days=${days}`),
  // Change slot for pending booking
  getAvailableSlots: (bookingId) => apiClient.get(`/external/approvals/${bookingId}/available-slots`),
  changeSpeakSlot: (bookingId, newSlotId) => {
    const formData = new FormData();
    formData.append('new_speak_slot_id', newSlotId);
    return apiClient.put(`/external/approvals/${bookingId}/speak-slot`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};
